import React, { useState, useRef } from 'react';
import {
  Modal, Form, Button, Image,
} from 'react-bootstrap';
import _ from 'lodash';
import Error from '../../../Components/Error';
import FormControlFeedback from '../../../Components/FormControlFeedback';
import setStateFromInput from '../../../Utils/setStateFromInput';
import BookingServicesService from '../../../Services/BookingServicesService';

const PRICE_TYPES = [
  { value: 'flat', label: 'Flat (one price regardless of passengers)' },
  { value: 'per_person', label: 'Per Person (multiplied by passenger count)' },
];

function BookingServiceInputModal({
  originalService = {},
  show = false,
  handleClose,
  fetchServices,
}) {
  const defaultService = {
    active: 1,
    visible_on_website: 1,
    name: '',
    description: '',
    name_translations: { hu: '', ru: '' },
    description_translations: { hu: '', ru: '' },
    price_eur: 0,
    price_huf: 0,
    price_type: 'flat',
    category: '',
    sort_order: 0,
  };

  const [service, setService] = useState(
    _.defaults({ ...originalService }, defaultService),
  );
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only PNG, JPEG, and WebP images are allowed');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image file size must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result.split(',')[1];

      if (service.Id) {
        setImageUploading(true);
        try {
          const result = await BookingServicesService.uploadBookingServiceImage(
            service.Id,
            base64,
            file.type,
          );
          setService((prev) => ({
            ...prev,
            image_key: result.data?.data?.image_key,
            image_url: result.data?.data?.image_url,
          }));
        } catch (err) {
          setError(err.response?.data?.message || 'Failed to upload image');
        } finally {
          setImageUploading(false);
        }
      } else {
        setService((prev) => ({
          ...prev,
          pendingImage: { base64, contentType: file.type },
          imagePreview: reader.result,
        }));
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = async () => {
    if (service.Id && service.image_key) {
      setImageUploading(true);
      try {
        await BookingServicesService.removeBookingServiceImage(service.Id);
        setService((prev) => ({
          ...prev,
          image_key: undefined,
          image_url: undefined,
        }));
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to remove image');
      } finally {
        setImageUploading(false);
      }
    } else {
      setService((prev) => ({
        ...prev,
        pendingImage: undefined,
        imagePreview: undefined,
      }));
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Drop empty translation strings so we don't persist `''` and
      // accidentally make the public endpoint serve a blank locale name.
      const cleanTranslations = (t) => {
        if (!t || typeof t !== 'object') return undefined;
        const out = {};
        if (t.hu?.trim?.()) out.hu = t.hu.trim();
        if (t.ru?.trim?.()) out.ru = t.ru.trim();
        return Object.keys(out).length > 0 ? out : undefined;
      };

      const payload = {
        ...service,
        active: Number(service.active),
        visible_on_website: Number(service.visible_on_website ?? 1),
        price_eur: Number(service.price_eur),
        price_huf: service.price_huf === '' || service.price_huf === null
          ? null
          : Number(service.price_huf),
        sort_order: Number(service.sort_order) || 0,
        name_translations: cleanTranslations(service.name_translations),
        description_translations: cleanTranslations(service.description_translations),
      };

      delete payload.pendingImage;
      delete payload.imagePreview;
      delete payload.image_url;

      const result = service.Id
        ? await BookingServicesService.updateBookingService(payload)
        : await BookingServicesService.createBookingService(payload);

      const savedService = result.data?.data;

      if (!service.Id && service.pendingImage && savedService?.Id) {
        try {
          await BookingServicesService.uploadBookingServiceImage(
            savedService.Id,
            service.pendingImage.base64,
            service.pendingImage.contentType,
          );
        } catch (imgErr) {
          // eslint-disable-next-line no-console
          console.error('Failed to upload image:', imgErr);
        }
      }

      fetchServices();
      handleClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save booking service');
      if (err.response?.data?.errors) {
        setValidationErrors(err.response.data.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setStateFromInput(e, setService, service);
  };

  const displayImage = service.image_url || service.imagePreview;

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {service.Id ? (
            <>
              Edit Service:
              {' '}
              {service.name}
            </>
          ) : (
            <>New Booking Service</>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Error error={error} setError={setError} />

        <Form>
          <div className="row">
            <Form.Group className="mb-3 col-md-8" controlId="name">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                value={service.name}
                onChange={handleChange}
                placeholder="e.g., Flowers"
              />
              <FormControlFeedback errors={validationErrors} field="name" />
            </Form.Group>

            <Form.Group className="mb-3 col-md-4" controlId="category">
              <Form.Label>Category</Form.Label>
              <Form.Control
                type="text"
                value={service.category || ''}
                onChange={handleChange}
                placeholder="e.g., gifts, beverage"
              />
            </Form.Group>

            <Form.Group className="mb-3 col-12" controlId="description">
              <Form.Label>Description (English)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={service.description || ''}
                onChange={handleChange}
                placeholder="Short description shown to customers in English"
              />
            </Form.Group>

            {/* Locale translations. Leaving any field blank means the
                public endpoint falls back to the English/admin default
                for that locale. Customer flow sends `?lang=` to pick. */}
            <div className="col-12 mb-2">
              <Form.Label className="text-muted small mb-1">
                Translations (optional)
              </Form.Label>
            </div>

            <Form.Group className="mb-3 col-md-6" controlId="name_translations.hu">
              <Form.Label className="small">Name (Hungarian)</Form.Label>
              <Form.Control
                type="text"
                value={service.name_translations?.hu || ''}
                onChange={handleChange}
                placeholder="e.g., Virágok"
              />
            </Form.Group>
            <Form.Group className="mb-3 col-md-6" controlId="name_translations.ru">
              <Form.Label className="small">Name (Russian)</Form.Label>
              <Form.Control
                type="text"
                value={service.name_translations?.ru || ''}
                onChange={handleChange}
                placeholder="e.g., Цветы"
              />
            </Form.Group>
            <Form.Group className="mb-3 col-md-6" controlId="description_translations.hu">
              <Form.Label className="small">Description (Hungarian)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={service.description_translations?.hu || ''}
                onChange={handleChange}
                placeholder="Magyar leírás"
              />
            </Form.Group>
            <Form.Group className="mb-3 col-md-6" controlId="description_translations.ru">
              <Form.Label className="small">Description (Russian)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={service.description_translations?.ru || ''}
                onChange={handleChange}
                placeholder="Русское описание"
              />
            </Form.Group>

            <Form.Group className="mb-3 col-md-4" controlId="price_eur">
              <Form.Label>Price (EUR) *</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                value={service.price_eur}
                onChange={handleChange}
              />
              <FormControlFeedback errors={validationErrors} field="price_eur" />
            </Form.Group>

            <Form.Group className="mb-3 col-md-4" controlId="price_huf">
              <Form.Label>Price (HUF)</Form.Label>
              <Form.Control
                type="number"
                step="1"
                min="0"
                value={service.price_huf ?? ''}
                onChange={handleChange}
              />
              <FormControlFeedback errors={validationErrors} field="price_huf" />
            </Form.Group>

            <Form.Group className="mb-3 col-md-4" controlId="price_type">
              <Form.Label>Price Type *</Form.Label>
              <Form.Select
                value={service.price_type}
                onChange={handleChange}
              >
                {PRICE_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3 col-md-4" controlId="sort_order">
              <Form.Label>Sort Order</Form.Label>
              <Form.Control
                type="number"
                value={service.sort_order || 0}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3 col-md-4" controlId="active">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={service.active}
                onChange={handleChange}
              >
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3 col-md-4" controlId="visible_on_website">
              <Form.Label>Visibility</Form.Label>
              <Form.Select
                value={service.visible_on_website ?? 1}
                onChange={handleChange}
              >
                <option value={1}>Visible on website (customers can pick)</option>
                <option value={0}>Internal only (admin-attached only)</option>
              </Form.Select>
              <Form.Text className="text-muted small">
                Internal-only services don&apos;t appear in the public booking flow
                but admins can still attach them to bookings from the dash.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3 col-md-4">
              <Form.Label>Image</Form.Label>
              <div className="d-flex align-items-start gap-2">
                {displayImage && (
                  <Image
                    src={displayImage}
                    rounded
                    style={{ width: 64, height: 64, objectFit: 'cover' }}
                  />
                )}
                <div className="flex-grow-1">
                  <Form.Control
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={handleImageSelect}
                    disabled={imageUploading}
                    size="sm"
                  />
                  {displayImage && (
                    <Button
                      size="sm"
                      variant="outline-danger"
                      className="mt-2"
                      onClick={handleRemoveImage}
                      disabled={imageUploading}
                    >
                      Remove image
                    </Button>
                  )}
                </div>
              </div>
            </Form.Group>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={loading || imageUploading}>
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default BookingServiceInputModal;
